import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { Button } from '../../../components/ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { isBreakpoint, useBreakpoint } from '../../../lib/hooks/use-breakpoints.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { cn } from '../../../lib/utils.ts';
import { FilterBadge } from './filter-badge';

export type AppliedFilter = {
    key: string;
    category?: string;
    filter?: string;
    isActive?: boolean;
    onRemove: () => void;
};

type Props = {
    onRemoveAllFilters: () => void;
    filters: AppliedFilter[];
    className?: string;
};

export const AppliedFilterList: React.FC<Props> = ({ filters, onRemoveAllFilters, className }) => {
    const { t } = useLocale();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const badgesContainerRef = useRef<HTMLDivElement>(null);
    const filterLabelRef = useRef<HTMLSpanElement>(null);
    const actionsButtonRef = useRef<HTMLDivElement>(null);
    const [maxVisibleFilters, setMaxVisibleFilters] = useState(3);

    const calculateMaxFilters = useCallback(() => {
        const badgesContainer = badgesContainerRef.current;
        const container = containerRef.current;

        if (!badgesContainer || !container) {
            return;
        }

        const isMd = isBreakpoint('md');
        if (!isMd) {
            setMaxVisibleFilters(0);
            return;
        }

        const badges = Array.from(badgesContainer.querySelectorAll('.filter-badge'));

        const maxBadgeWidth = Math.max(...badges.map((badge) => badge.clientWidth), 1);
        const filterLabelWidth = filterLabelRef.current?.clientWidth ?? 0;
        const actionsButtonWidth = actionsButtonRef.current?.clientWidth ?? 0;
        const availableSpace =
            container.clientWidth -
            filterLabelWidth -
            actionsButtonWidth -
            // view more button;
            100 -
            // We have 4 containers with 12px gap
            12 * 4;

        setMaxVisibleFilters(Math.max(1, Math.floor(availableSpace / maxBadgeWidth)));
    }, []);

    const debouncedCalculateMaxFilters = useDebounceCallback(calculateMaxFilters, 200);

    useEffect(() => {
        calculateMaxFilters();

        window.addEventListener('resize', debouncedCalculateMaxFilters);
        return () => {
            window.removeEventListener('resize', debouncedCalculateMaxFilters);
        };
    }, [calculateMaxFilters, debouncedCalculateMaxFilters]);

    const visibleFilters = filters.slice(0, maxVisibleFilters);
    const hiddenFilters = filters.slice(maxVisibleFilters);
    const hasHiddenFilters = hiddenFilters.length > 0;

    if (filters.length === 0) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className={cn('flex flex-row items-center gap-3 gap-y-1 align-middle', className)}
        >
            <Text ref={filterLabelRef} className="flex shrink-0" weight="medium">
                {t('filter_header__title', {
                    ns: 'generic-list',
                })}
                :
            </Text>
            <div className="flex flex-row min-w-0 overflow-hidden gap-2">
                <div className="flex grow flex-row gap-2" ref={badgesContainerRef}>
                    {visibleFilters.map(({ key, category, filter, isActive, onRemove }) => (
                        <FilterBadge
                            key={key}
                            category={category}
                            filter={filter}
                            isActive={isActive}
                            onRemove={onRemove}
                            className="filter-badge max-w-xs" // we don't want the badges to grow too much
                        />
                    ))}
                </div>
                {hasHiddenFilters && (
                    <Popover open={isOpen} onOpenChange={setIsOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className="h-auto bg-blue-50 px-2 py-1 text-xs font-medium text-neutral-700"
                                rightIcon={ChevronDownIcon}
                                rightIconProps={{
                                    rotateOnActive: true,
                                }}
                            >
                                +{hiddenFilters.length} {t('more')}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2" align="start">
                            <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto">
                                {hiddenFilters.map(({ key, category, filter, isActive, onRemove }) => (
                                    <div className="flex w-full m-w-full" key={key}>
                                        <FilterBadge
                                            key={key}
                                            category={category}
                                            filter={filter}
                                            isActive={isActive}
                                            onRemove={onRemove}
                                        />
                                    </div>
                                ))}
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
            <div ref={actionsButtonRef} className="flex flex-row gap-3">
                <Button variant="link" onClick={onRemoveAllFilters} className="text-blue-600">
                    {t('filter_header__delete_all_button', {
                        ns: 'generic-list',
                    })}
                </Button>
            </div>
        </div>
    );
};
